import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { canDeleteInPortal } from "./portalSession";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function fullName(row) {
  return `${(row.First_Name || "").trim()} ${(row.Last_Name || "").trim()}`.trim() || "—";
}

const SORT_ACCESSORS = {
  name: (e) => fullName(e).toLowerCase(),
  employeeId: (e) => (e.Employee_Id || "").toLowerCase(),
  designation: (e) => (e.Designation || "").toLowerCase(),
  department: (e) => (e.Department || "").toLowerCase(),
  email: (e) => ((e.Company_Email || e.Email) || "").toLowerCase(),
  phone: (e) => (e.Phone_Number || "").toLowerCase(),
  status: (e) => (e.Status || "").toLowerCase(),
};

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "employeeId", label: "Employee ID" },
  { key: "designation", label: "Designation" },
  { key: "department", label: "Department" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  companyEmail: "",
  password: "",
  employeeId: "",
  phoneNumber: "",
  department: "",
  designationId: "",
  gender: "",
  doj: "",
};

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];

function validateEmployeeForm(form) {
  if (!form.firstName.trim() || !form.email.trim()) {
    return "First name and email are required.";
  }
  if (!form.companyEmail.trim()) {
    return "Company email is required.";
  }
  if (!form.employeeId.trim()) {
    return "Employee ID is required.";
  }
  const phoneError = mobileValidationMessage(form.phoneNumber, { required: true });
  if (phoneError) return phoneError;
  if (!form.designationId) {
    return "Designation is required.";
  }
  if (!form.password.trim()) {
    return "Password is required.";
  }
  return "";
}

function CustomerEmployees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const loadEmployees = useCallback(async () => {
    const res = await axiosInstance.get("/client/employees");
    setEmployees(Array.isArray(res.data?.data) ? res.data.data : []);
    const tid = res.data?.tenant_id ?? res.data?.Tenant_Id ?? null;
    if (tid != null) setTenantId(tid);
    return tid;
  }, []);

  const loadMeta = useCallback(async () => {
    const res = await axiosInstance.get("/client/employee-meta");
    const data = res.data?.data || {};
    const tid = data.tenant_id ?? data.Tenant_Id ?? null;
    if (tid != null) setTenantId(tid);
    setDesignations(Array.isArray(data.designations) ? data.designations : []);
    return tid;
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadEmployees();
      } catch (_) {
        if (active) setError("Could not load employees.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadEmployees]);

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return employees;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...employees].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [employees, sortKey, sortDir]);

  const openAddModal = async () => {
    setFormError("");
    setForm(EMPTY_FORM);
    setShowAddModal(true);
    try {
      await loadMeta();
    } catch (_) {
      setFormError("Could not load form options.");
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleDeactivate = async (emp) => {
    if (emp.Status === "Inactive") return;
    if (!window.confirm(`Deactivate ${fullName(emp)}? Their status will be set to Inactive.`)) return;

    setDeactivatingId(emp.Tenant_User_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/employees/${emp.Tenant_User_Id}`);
      await loadEmployees();
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate employee.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateEmployeeForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (!tenantId) {
      setFormError("Your company tenant is not set up. Contact support.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/employees", {
        Tenant_Id: tenantId,
        First_Name: form.firstName.trim(),
        Last_Name: form.lastName.trim() || null,
        Email: form.email.trim(),
        Company_Email: form.companyEmail.trim(),
        Password: form.password.trim(),
        Employee_Id: form.employeeId.trim(),
        Phone_Number: form.phoneNumber.trim(),
        Department: form.department.trim() || null,
        Designation_Id: Number(form.designationId),
        Gender: form.gender.trim() || null,
        DOJ: form.doj.trim() || null,
        Status: "Active",
      });
      await loadEmployees();
      closeAddModal();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create employee."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h1>Employees</h1>
        <button
          type="button"
          className="cportal-btn cportal-btn-primary"
          onClick={openAddModal}
        >
          Add employee
        </button>
      </div>

      {error && <div className="cportal-alert cportal-alert-error">{error}</div>}

      <div className="cportal-card">
        <div className="cportal-card-head">
          <span className="cportal-muted">{sorted.length} employee(s)</span>
        </div>

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No employees are registered for your company yet.</p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((emp) => (
                  <tr key={emp.Tenant_User_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/employees/${emp.Tenant_User_Id}`)}
                      >
                        {display(emp.Full_Name || fullName(emp))}
                      </button>
                    </td>
                    <td>{display(emp.Employee_Id)}</td>
                    <td>{display(emp.Designation)}</td>
                    <td>{display(emp.Department)}</td>
                    <td>{display(emp.Company_Email || emp.Email)}</td>
                    <td>{display(emp.Phone_Number)}</td>
                    <td>{display(emp.Status)}</td>
                    <td>
                      <div className="cportal-re-actions">
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/employees/${emp.Tenant_User_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit employee"
                          aria-label="Edit employee"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => handleDeactivate(emp)}
                            disabled={
                              emp.Status === "Inactive" ||
                              deactivatingId === emp.Tenant_User_Id
                            }
                            title="Delete employee"
                            aria-label="Delete employee"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="cportal-modal-overlay" onClick={closeAddModal} role="presentation">
          <div
            className="cportal-modal cportal-modal--form"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-employee-title"
          >
            <div className="cportal-modal-header">
              <h3 id="add-employee-title">Add employee</h3>
              <button type="button" onClick={closeAddModal} aria-label="Close">
                ✕
              </button>
            </div>
            <form className="cportal-modal-body" onSubmit={handleSubmit} noValidate>
              {formError && (
                <div className="cportal-alert cportal-alert-error">{formError}</div>
              )}
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    First name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Last name</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Email <span className="cportal-required">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Company email <span className="cportal-required">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.companyEmail}
                    onChange={(e) => setField("companyEmail", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.phoneNumber}
                    onChange={(e) => setField("phoneNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Password <span className="cportal-required">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    maxLength={20}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Employee ID <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.employeeId}
                    onChange={(e) => setField("employeeId", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Gender</label>
                  <select
                    value={form.gender}
                    onChange={(e) => setField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field">
                  <label>Department</label>
                  <input
                    value={form.department}
                    onChange={(e) => setField("department", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Designation <span className="cportal-required">*</span>
                  </label>
                  <select
                    value={form.designationId}
                    onChange={(e) => setField("designationId", e.target.value)}
                    required
                  >
                    <option value="">Select designation</option>
                    {designations.map((d) => (
                      <option key={d.Designation_Id} value={d.Designation_Id}>
                        {d.Designation_Name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cportal-field">
                  <label>Date of joining</label>
                  <input
                    type="date"
                    value={form.doj}
                    onChange={(e) => setField("doj", e.target.value)}
                  />
                </div>
              </div>
              <div className="cportal-form-actions">
                <button
                  type="button"
                  className="cportal-btn cportal-btn-ghost"
                  onClick={closeAddModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="cportal-btn cportal-btn-primary" disabled={submitting}>
                  {submitting ? "Creating…" : "Create employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerEmployees;
